/**
 * A minimal stored-vs-exported diff for the preview.
 *
 * The encodings mostly rewrap or trim a value rather than rewriting it: a GMC
 * resource name loses its path prefix, a JSON `{"content":"…"}` becomes
 * `type:"…"`, a boolean is normalised. In every one of those the interesting
 * part is small and the identical part is long — so showing two full strings
 * and leaving the reader to compare them character by character buries the
 * answer in noise.
 *
 * Anchoring on the common prefix and suffix isolates exactly the span that
 * moved. It is not a general-purpose diff (no LCS, no per-word alignment) and
 * deliberately so: those produce speckled, hard-to-scan output on strings like
 * these, where one contiguous edit is the norm.
 */
export interface ValueDiff {
  /** Leading text both values share. */
  prefix: string;
  /** The span present only in the stored value. */
  removed: string;
  /** The span present only in the exported value. */
  added: string;
  /** Trailing text both values share. */
  suffix: string;
  /** False when the two values are identical — nothing to highlight. */
  changed: boolean;
}

/** Longest common prefix length of two strings. */
function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
}

/**
 * Longest common suffix length, bounded so the prefix and suffix cannot claim
 * the same characters. Without the bound, `"aa" -> "aaa"` would report a
 * 2-character prefix AND a 2-character suffix out of a 2-character string, and
 * the middle span would come back negative.
 */
function commonSuffixLength(a: string, b: string, prefixLength: number): number {
  const max = Math.min(a.length, b.length) - prefixLength;
  let i = 0;
  while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
}

/**
 * Splits *stored* and *exported* into shared and differing spans.
 *
 * An empty side is meaningful and preserved: a value the encoding dropped
 * entirely has `added === ''`, and one it created from nothing has
 * `removed === ''`. Callers render those as a removal or an addition rather
 * than as a replacement.
 */
export function diffValues(stored: string, exported: string): ValueDiff {
  if (stored === exported) {
    return { prefix: stored, removed: '', added: '', suffix: '', changed: false };
  }
  const prefixLength = commonPrefixLength(stored, exported);
  const suffixLength = commonSuffixLength(stored, exported, prefixLength);
  return {
    prefix: stored.slice(0, prefixLength),
    removed: stored.slice(prefixLength, stored.length - suffixLength),
    added: exported.slice(prefixLength, exported.length - suffixLength),
    suffix: stored.slice(stored.length - suffixLength),
    changed: true,
  };
}

/**
 * Whether the diff has something both values share to anchor an inline display.
 *
 * This replaced a "how much of the value changed" ratio, which got the most
 * important case backwards: stripping `accounts/5354994054/products/en~US~`
 * off a resource name changes ~70% of the string, so a ratio test rejected it
 * as a wholesale rewrite — when it is the single clearest thing to show inline,
 * because the part that survives is right there at the end.
 *
 * What actually decides it is whether there IS an anchor. With a shared prefix
 * or suffix, one contiguous span moved and marking it in place reads
 * immediately. With neither, the two values have no common ground to align on
 * and a "diff" would just be both strings fully highlighted — a labelled pair
 * is the honest presentation there.
 */
export function hasAnchor(diff: ValueDiff): boolean {
  return diff.changed && (diff.prefix !== '' || diff.suffix !== '');
}
